/** Second-person / buyer-facing text for saved search UI (not agent CRM copy). */

const CRITERIA_CLEAR = '__CLEAR__';

const PROPERTY_TYPE_LABELS = {
  single_family: 'detached',
  detached: 'detached',
  semi_detached: 'semi-detached',
  townhouse: 'townhouse',
  condo: 'condo',
  condominium: 'condo',
  duplex: 'duplex',
  bungalow: 'bungalow',
  cottage: 'cottage',
  mobile: 'mobile / mini home',
  vacant_land: 'vacant land',
  luxury_detached: 'luxury detached',
};

function stripClearSentinel(value) {
  if (value == null) return value;
  if (value === CRITERIA_CLEAR) return null;
  if (typeof value === 'string' && value.trim() === CRITERIA_CLEAR) return null;
  if (Array.isArray(value)) {
    return value.filter(
      (v) => v != null && String(v).trim() !== '' && String(v).trim() !== CRITERIA_CLEAR
    );
  }
  return value;
}

function formatPropertyTypeLabel(type) {
  const raw = stripClearSentinel(type);
  if (raw == null || raw === '') return null;
  const key = String(raw).trim();
  const lower = key.toLowerCase();
  return PROPERTY_TYPE_LABELS[lower] || key.replace(/_/g, ' ');
}

export function normalizeBuyerSummaryText(text) {
  if (!text || typeof text !== 'string') return null;
  let s = text.trim();
  if (s === CRITERIA_CLEAR) return null;
  s = s.replace(/^Buyer wants\b/i, "You're looking for");
  s = s.replace(/^The buyer wants\b/i, "You're looking for");
  s = s.replace(/^Buyer is looking for\b/i, "You're looking for");
  s = s.replace(/^Buyer (?:is|was) seeking\b/i, "You're looking for");
  s = s.replace(/\bthe buyer\b/gi, 'you');
  if (/^you/i.test(s)) s = s.charAt(0).toUpperCase() + s.slice(1);
  return s;
}

export function formatBuyerCriteriaLine(buyerProfile) {
  if (!buyerProfile) return null;
  const parts = [];
  if (buyerProfile.price_max) {
    const min = buyerProfile.price_min;
    parts.push(
      min
        ? `$${Number(min).toLocaleString()}–$${Number(buyerProfile.price_max).toLocaleString()}`
        : `up to $${Number(buyerProfile.price_max).toLocaleString()}`
    );
  } else if (buyerProfile.price_min) {
    parts.push(`from $${Number(buyerProfile.price_min).toLocaleString()}`);
  }
  if (buyerProfile.bedrooms_min) parts.push(`${buyerProfile.bedrooms_min}+ bedrooms`);
  if (buyerProfile.bathrooms_min) parts.push(`${buyerProfile.bathrooms_min}+ baths`);
  const types = stripClearSentinel(buyerProfile.property_types);
  if (Array.isArray(types) && types.length) {
    const labels = types.map(formatPropertyTypeLabel).filter(Boolean);
    if (labels.length) parts.push(labels.join(', '));
  }
  const areas = stripClearSentinel(buyerProfile.areas);
  if (Array.isArray(areas) && areas.length) {
    parts.push(areas.join(', '));
  }
  const mustHaves = stripClearSentinel(buyerProfile.must_haves);
  if (Array.isArray(mustHaves) && mustHaves.length) {
    parts.push(`must-haves: ${mustHaves.slice(0, 4).join(', ')}`);
  }
  const avoid = stripClearSentinel(buyerProfile.deal_breakers);
  if (Array.isArray(avoid) && avoid.length) {
    parts.push(`avoid: ${avoid.slice(0, 4).join(', ')}`);
  }
  const nice = stripClearSentinel(
    buyerProfile.nice_to_haves || buyerProfile.preferences?.nice_to_haves
  );
  if (Array.isArray(nice) && nice.length) {
    parts.push(`nice to have: ${nice.slice(0, 4).join(', ')}`);
  }
  const areaPriorities = stripClearSentinel(buyerProfile.area_priorities);
  if (Array.isArray(areaPriorities) && areaPriorities.length) {
    parts.push(areaPriorities.join(', '));
  }
  const timeline = stripClearSentinel(buyerProfile.timeline);
  if (timeline) {
    const t = String(timeline).trim();
    parts.push(/^timeline\s*:/i.test(t) ? t : `Timeline: ${t}`);
  }
  return parts.length ? parts.join(' · ') : null;
}

export function getBuyerNarrativeSummary(buyerProfile) {
  if (!buyerProfile) return null;
  const raw =
    buyerProfile.ai_summary
    || buyerProfile.preferences?.summary
    || null;
  return normalizeBuyerSummaryText(raw);
}
